import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  TextInput,
} from "flowbite-react";
import { useEffect, useState } from "react";
import { SquareX, Trash2, CheckCircle } from "lucide-react";
import axiosInstance from "../config/config";

export function JobItemsModal({ closeModal, job }) {
  const [jobItems, setJobItems] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [itemName, setItemName] = useState("");
  const [qty, setQty] = useState(1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchJobItems = async () => {
    try {
      const response = await axiosInstance.post("", {
        SysID: `select * from dbo.job_job_item where job_id=${job.id};`,
      });
      setJobItems(response.data || []);
    } catch (error) {
      console.error("Error fetching job items:", error);
    }
  };

  useEffect(() => {
    fetchJobItems();
    const loadMasterItems = async () => {
      try {
        const response = await axiosInstance.post("", {
          SysID: `select * from dbo.job_item;`,
        });
        setMasterItems(response.data || []);
      } catch (error) {
        console.error("Error fetching master items:", error);
      }
    };
    loadMasterItems();
  }, []);

  const handleAddItem = async () => {
    if (!itemName.trim() || qty < 1) return;
    setLoading(true);
    try {
      await axiosInstance.post("", {
        SysID: `EXEC dbo.add_job_job_item @job_id=${job.id}, @item_name='${itemName}', @qty=${qty}; SELECT 1`,
      });
      setItemName("");
      setQty(1);
      fetchJobItems();
    } catch (error) {
      console.error("Error adding item:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.post("", {
        SysID: `EXEC dbo.del_job_job_item @id=${id}; SELECT 1`,
      });
      fetchJobItems();
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const handleReturn = async (id) => {
    try {
      await axiosInstance.post("", {
        SysID: `EXEC dbo.ret_job_job_item @id=${id}; SELECT 1`,
      });
      fetchJobItems();
    } catch (error) {
      console.error("Error returning item:", error);
    }
  };

  const filteredMaster = masterItems.filter((m) =>
    m.item_name.toLowerCase().includes(itemName.toLowerCase())
  );

  const pendingCount = jobItems.filter((i) => !i.is_returned).length;
  const returnedCount = jobItems.filter((i) => i.is_returned).length;

  return (
    <Modal show={true} size="lg" onClose={closeModal} popup>
      <ModalHeader />
      <ModalBody>
        <div className="space-y-4">
          <div className="flex items-start">
            <div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                Items — {job.j_name}
              </h3>
              <p className="text-sm text-gray-500">{job.c_name}</p>
            </div>
            <SquareX
              onClick={closeModal}
              className="ml-auto text-red-600 hover:cursor-pointer"
            />
          </div>

          {/* Summary badges */}
          {jobItems.length > 0 && (
            <div className="flex gap-3">
              <span className="bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                {pendingCount} pending return
              </span>
              <span className="bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full">
                {returnedCount} returned
              </span>
            </div>
          )}

          {/* Items list */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {jobItems.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-4">
                No items added yet.
              </p>
            )}
            {jobItems.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  item.is_returned
                    ? "bg-green-50 dark:bg-green-900/20 border border-green-200"
                    : "bg-gray-50 dark:bg-gray-700"
                }`}
              >
                <div className="flex-1">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {item.item_name}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">x{item.qty}</span>
                </div>
                {item.is_returned ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                    <CheckCircle size={16} /> Returned
                  </span>
                ) : (
                  <button
                    onClick={() => handleReturn(item.id)}
                    className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 whitespace-nowrap"
                  >
                    Mark Returned
                  </button>
                )}
                {!item.is_returned && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-500 hover:text-red-700 ml-1"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add item */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Add Item
            </p>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <TextInput
                  placeholder="Search master list or type name..."
                  value={itemName}
                  onChange={(e) => {
                    setItemName(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                />
                {showSuggestions && itemName && filteredMaster.length > 0 && (
                  <div className="absolute z-50 w-full border rounded-lg mt-1 max-h-32 overflow-y-auto bg-white shadow-lg">
                    {filteredMaster.map((m) => (
                      <div
                        key={m.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-800 text-sm"
                        onClick={() => {
                          setItemName(m.item_name);
                          setShowSuggestions(false);
                        }}
                      >
                        {m.item_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                className="w-16 rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Qty"
              />
              <Button onClick={handleAddItem} disabled={loading}>
                Add
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
